import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import React from 'react'
import ChartIcon from '@mui/icons-material/Addchart'
import { useAuth } from '../../../context/useAuth'
import { MemoDashboard } from '../../charts/Dashboard'
import ButtonDialog from '../../shared/ButtonDialog'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import axios from 'axios'

interface Props {
  visible: boolean
}

export default function Results({ visible }: Props) {
  const { simulation } = useAuth()
  const [ready, setReady] = React.useState<boolean>(false)
  const [error, setError] = React.useState<any>(undefined)
  const [open, setOpen] = React.useState<boolean>(false)
  const [openDownload, setOpenDownload] = React.useState<boolean>(false)

  const getDialogContent = () => {
    switch (simulation?.status?.progress) {
      case 100:
        return (
          <>
            <Typography variant='body2'>
              If you encounter errors when fetching results, this might indicate you have changed the config without
              re-running the simulation.
            </Typography>
            <br />
            <Typography variant='body2'>After running the simulation again, these errors should disappear.</Typography>
            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setReady(true)
                }}
              >
                View
              </Button>
            </DialogActions>
          </>
        )
      case 0:
        return (
          <>
            <Typography variant='body2'>In order to view results, you need to run the simulation first.</Typography>
            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpen(false)}>Ok</Button>
            </DialogActions>
          </>
        )
      default:
        return (
          <>
            <Typography variant='body2' color='error'>
              {`Errors were encountered when running the simulation, only ${Math.round(simulation?.status.progress * 100.0) / 100
                }% of results will be available.`}
            </Typography>

            <br />
            <Typography variant='body2'>Are you sure you want to view these results?</Typography>
            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setReady(true)
                }}
              >
                View
              </Button>
            </DialogActions>
          </>
        )
    }
  }

  const getDialogContentDownload = () => {
    switch (simulation?.status?.progress) {
      case 100:
        return (
          <>
            <Typography variant='body2'>
              If you encounter errors when downloading results, this might indicate you have changed the config without
              re-running the scenario.
            </Typography>

            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpenDownload(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  setOpenDownload(false)
                  try {
                    const res = await axios.get(
                      `${process.env.REACT_APP_API}/${simulation.simulation_name}/results/download/`,
                      {
                        withCredentials: true,
                        responseType: 'blob',
                      }
                    )
                    const url = window.URL.createObjectURL(new Blob([res.data]))
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', simulation.simulation_name + '.zip')
                    document.body.appendChild(link)
                    link.click()
                  } catch (err) {
                    console.log('err:', err)
                  }
                }}
              >
                Download
              </Button>
            </DialogActions>
          </>
        )
      case 0:
        return (
          <>
            <Typography variant='body2'>In order to download results, you need to run the scenario first.</Typography>
            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpenDownload(false)}>Ok</Button>
            </DialogActions>
          </>
        )
      default:
        return (
          <>
            <Typography variant='body2' color='error'>
              {`Errors were encountered when running the scenario, only ${Math.round(simulation?.status.progress * 100.0) / 100
                }% of results will be available.`}
            </Typography>
            <br />
            <Typography variant='body2'>Are you sure you want to download these results?</Typography>
            
            <DialogActions sx={{ p: 0, pt: 2 }}>
              <Button onClick={() => setOpenDownload(false)}>Cancel</Button>
              <Button
                onClick={async () => {
                  setOpenDownload(false)
                  try {
                    const res = await axios.get(
                      `${process.env.REACT_APP_API}/${simulation.simulation_name}/results/download/`,
                      {
                        withCredentials: true,
                        responseType: 'blob',
                      }
                    )
                    const url = window.URL.createObjectURL(new Blob([res.data]))
                    const link = document.createElement('a')
                    link.href = url
                    link.setAttribute('download', simulation.simulation_name + '.zip')
                    document.body.appendChild(link)
                    link.click()
                  } catch (err) {
                    console.log('err:', err)
                  }
                }}
              >
                Download
              </Button>
            </DialogActions>
          </>
        )
    }
  }

  return (
    <Stack sx={{ display: visible ? 'flex' : 'none' }}>
      {!ready && (
        <>
          <ButtonDialog
            disabled={simulation?.status?.running}
            title={`View Results`}
            icon={<ChartIcon />}
            content={getDialogContent()}
            open={open}
            setOpen={setOpen}
          />
          <ButtonDialog
            disabled={simulation?.status?.running}
            title={`Download Results`}
            icon={<ChartIcon />}
            content={getDialogContentDownload()}
            open={openDownload}
            setOpen={setOpenDownload}
          />
        </>

      )}

      {error && (
        <Typography variant='caption' color='error' sx={{ mt: 1, pl: 2, pb: 1 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ p: 2 }}>
        <MemoDashboard ready={ready} setError={setError} />
      </Box>
    </Stack>
  )
}
